import React, { useEffect, useState } from "react";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import useHost from "../useHooks";
import Pagination from "components/pagination";
import { useNavigate } from "react-router-dom";
import useStore from "hooks/useStore";
import { useSelector } from "react-redux";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

class ManagePaymentsFiltersDTO {
  page: number = 0;
  size: number = 10;
}

const ManagePayments: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "Manage Payments", path: siteRoutes.managePayments },
  ];
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const [filters, setFilters] = useState<ManagePaymentsFiltersDTO>(
    new ManagePaymentsFiltersDTO()
  );

  const {
    getHostBalance,
    getHostEarnings,
    postStripeConnect,
    getStripeAccountStatus,
    getHostEarningsOnboarding,
    getStripDashboardLink,
  } = useHost();
  const [hostBalance, setHostBalance] = useState<any>(null);
  const [hostEarnings, setHostEarnings] = useState<any[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<any>(null);
  const [stripDashboardLink, setStripDashboardLink] = useState<any>(null);
  const [hostEarningsOnboarding, setHostEarningsOnboarding] =
    useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    const updatedFilters = {
      ...filters,
      page: pageInfo.selected,
      size: pageInfo.limit,
    };
    setFilters(updatedFilters);
    getAllData(updatedFilters);
  };

  const getAllData = (filters: ManagePaymentsFiltersDTO) => {
    const queryParams: any = {};
    for (let key in filters) {
      const value = filters[key];
      // Include page and size always, include status only if it has a value
      if (
        key === "page" ||
        key === "size" ||
        (value !== undefined && value !== "" && value !== null)
      ) {
        queryParams[key] = value;
      }
    }
    getHostEarnings(setHostEarnings, queryParams,  setTotalElements);

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    getAllData(filters);
  }, []);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  useEffect(() => {
    getHostBalance(setHostBalance);
  }, []);

  const handleStripeConnect = () => {
    postStripeConnect();
  };

  const handleStripeConnectStatus = () => {
    getStripeAccountStatus(setStripeAccountStatus);
  };

  const handleManageAccount = async () => {
    const statusResponse = await getStripeAccountStatus(
      setStripeAccountStatus,
      setHostBalance
    );

    const isVerified = statusResponse?.data?.verified === true;

    if (!isVerified) {
      const response = await getHostEarningsOnboarding(
        setHostEarningsOnboarding
      );
      if (response?.data?.onboardingUrl) {
        window.open(response.data.onboardingUrl, "_blank");
      }
    } else {
      const dashboardResponse = await getStripDashboardLink(
        setStripDashboardLink
      );
      if (dashboardResponse?.data?.dashboardUrl) {
        window.open(dashboardResponse.data.dashboardUrl, "_blank");
      }
    }
  };

  return (
    <div className="container min-h-screen">
      <div className="mb-6">
        <Breadcrumb links={breadcrumbLinks} />
      </div>
      <div className="flex items-center flex-wrap justify-between gap-4 mb-6">
        <h1 className="text-2xl text-blackie font-medium ">Manage Payments</h1>
        <div className="flex items-center gap-4">
          <div>
            {hostBalance?.stripeAccountVerified ? (
              <button
                onClick={handleManageAccount}
                className="h-[54px] bg-primaryBlueBg text-lg items-center px-6 py-1 rounded-full text-white"
              >
                Manage Account
              </button>
            ) : (
              <button
                onClick={handleStripeConnect}
                className="h-[54px] bg-primaryBlueBg text-lg items-center px-6 py-1 rounded-full text-white"
              >
                Connect Account
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 my-12">
        <div className="flex flex-col justify-between px-6 py-8 border border-borderColor rounded-lg w-[234px] h-[152px]">
          <h2 className="text-base text-blackie">Total Balance</h2>
          <p className="text-2xl font-semibold text-blackie">
            ${Number(hostBalance?.totalEarnings || 0).toFixed(2)} USD
          </p>
        </div>
        <div className="flex flex-col justify-between px-6 py-8 border border-borderColor rounded-lg w-[234px] h-[152px]">
          <h2 className="text-base text-blackie">Pending Balance</h2>
          <p className="text-2xl font-semibold text-blackie">
            ${Number(hostBalance?.totalLockedPayments || 0).toFixed(2)} USD
          </p>
        </div>
        <div className="flex flex-col justify-between px-6 py-8 border border-borderColor rounded-lg w-[234px] h-[152px]">
          <h2 className="text-base text-blackie">Transferable Balance</h2>
          <p className="text-2xl font-semibold text-blackie">
            ${Number(hostBalance?.availableBalance || 0).toFixed(2)} USD
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Transaction ID</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                  Event Title
                </th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                  Payer Name
                </th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {hostEarnings.length > 0
                ? hostEarnings.map((earning: any, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="px-4 py-2 text-sm">
                        {earning?.event?.title || "N/A"}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {earning.createdAt
                          ? new Date(earning.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "N/A"}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            earning.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : earning.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {earning.status || "Unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {earning.payer?.firstName || "N/A"}{" "}
                        {earning.payer?.lastName || "N/A"}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        ${earning.hostEarnings || "0.00"} USD
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
      </div>

      <CustomMessageDisplay
        show={!isLoading && !hostEarnings.length && !isInitialLoad}
        title="No Transactions Available"
        slogan="You don't have any payment transactions yet. Transactions will appear here once attendees make payments for your experiences."
        image={noFoundGif}
        imageAlt="No transactions found"
        className="py-12"
        imageClassName="w-24 h-24"
        titleClassName="text-2xl font-bold text-blackie"
        sloganClassName="text-gray-500 text-base"
      />

      {hostEarnings?.length > 0 && (
        <Pagination
          key={`pagination-${filters.page}-${filters.size}`}
          count={totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
    </div>
  );
};

export default ManagePayments;
